import { ComponentType, type ComponentTypes } from '@/lib/types';
import Bubble, { type BubbleProps } from './bubble/bubble';
import Checkbox, { type CheckboxProps } from './checkbox/checkbox';
import Dropdown, { type DropdownProps } from './dropdown/dropdown';
import Input, { type FoundationInputProps } from './input/input';
import Radio, { type RadioProps } from './radio/radio';
import Switch, { SwitchProps } from './switch/switch';
import { Button, type ButtonProps } from './ui/button';
import { Separator, type SeparatorProps } from './ui/separator';
import FoundationAvatar, { type AvatarProps } from './avatar/avatar';
import List, { type ListProps } from './list/list';
import type {
	ChatMutationType,
	ChatWindowSourceType,
} from './chat-window/chat-window';
import { Dispatch, SetStateAction } from 'react';

type ComponentPropsMap = {
	Avatar: AvatarProps;
	Button: ButtonProps;
	Switch: SwitchProps;
	Radio: RadioProps;
	Input: FoundationInputProps;
	Bubble: BubbleProps;
	Checkbox: CheckboxProps;
	Dropdown: DropdownProps;
	Separator: SeparatorProps;
	List: ListProps;
};

interface ComponentRendererProps {
	type: ComponentTypes;
	props: ComponentPropsMap[keyof ComponentPropsMap];
	firstItem?: boolean;
}

const ComponentRenderer = ({
	components,
	refresh,
	setChatWindowSources,
	chatWindowSources,
}: {
	components: ComponentRendererProps[];
	refresh?: ChatMutationType;
	setChatWindowSources?: Dispatch<SetStateAction<ChatWindowSourceType>>;
	chatWindowSources?: ChatWindowSourceType;
}) => {
	const renderComponent = ({
		component,
		index,
	}: {
		component: ComponentRendererProps;
		index: number;
	}) => {
		switch (component.type) {
			case ComponentType.Avatar:
				return <FoundationAvatar {...(component.props as AvatarProps)} />;
			case ComponentType.Button:
				return <Button {...(component.props as ButtonProps)} />;
			case ComponentType.Switch:
				return <Switch {...(component.props as SwitchProps)} />;
			case ComponentType.Radio:
				return <Radio {...(component.props as RadioProps)} />;
			case ComponentType.Input:
				return <Input {...(component.props as FoundationInputProps)} />;
			case ComponentType.Bubble:
				return (
					<Bubble
						{...{
							chatWindowSources: chatWindowSources,
							setChatWindowSources: setChatWindowSources,
							refresh: refresh,
							firstItem: index === 0,
							index: index,
							props: { ...(component.props as BubbleProps) },
						}}
					/>
				);
			case ComponentType.Checkbox:
				return <Checkbox {...(component.props as CheckboxProps)} />;
			case ComponentType.Dropdown:
				return <Dropdown {...(component.props as DropdownProps)} />;
			case ComponentType.Separator:
				return <Separator {...(component.props as SeparatorProps)} />;
			case ComponentType.List:
				return <List {...(component.props as ListProps)} />;
			default:
				return null;
		}
	};

	return (
		<div
			id='chat-window-components'
			className='wlcb-flex wlcb-flex-col wlcb-gap-y-4 wlcb-w-full'
		>
			{components?.map((component, index) => (
				<div
					className='wlcb-w-full'
					key={index}
				>
					{renderComponent({ component, index })}
				</div>
			))}
		</div>
	);
};
export default ComponentRenderer;
